// docs/40 — items in the encounter: the consumable catalogue (`fk-sim-wasm.consumables()`, the
// sim's own effects), the bag a hero carries into the fight (`Snapshot.bags`), and the plain data
// the HUD's inventory and haul panels draw. Pure functions; covered by items.test.ts.
import type { BagView, ResourceName } from './types';
import { catalogueItem, fightUse, itemAbilityId } from '../items/catalogue';
import { describeItem, shortLine } from '../items/describe';

/** `fk_grammar::ConsumableUse` as serde hands it over: `{ Heal: 4 }`, `{ Resource: 2 }`, `{ Hidden: 1 }`, `'Reveal'`. */
export type ConsumableEffect = { kind: 'heal'; amount: number } | { kind: 'resource'; amount: number } | { kind: 'hidden'; pulses: number } | { kind: 'reveal' };
export interface ConsumableDef { def: number; name: string; effect: ConsumableEffect; reachesAFriend: boolean }

interface WasmConsumable { def: number; name: string; effect: Record<string, number> | string; reaches_a_friend: boolean }

export function consumablesFromWasm(list: WasmConsumable[]): ConsumableDef[] {
  return list.map((c) => {
    const k = typeof c.effect === 'string' ? c.effect : Object.keys(c.effect)[0] ?? '';
    const v = typeof c.effect === 'object' ? Number(c.effect[k] ?? 0) : 0;
    const effect: ConsumableEffect = k === 'Heal' ? { kind: 'heal', amount: v } : k === 'Resource' ? { kind: 'resource', amount: v } : k === 'Reveal' ? { kind: 'reveal' } : { kind: 'hidden', pulses: v };
    return { def: Number(c.def), name: String(c.name), effect, reachesAFriend: Boolean(c.reaches_a_friend) };
  });
}

/** Until the wasm catalogue is installed (tests, the gallery): 10 §6's three and docs/52's scroll. */
let catalogue: ConsumableDef[] = [
  { def: 160, name: 'Salve', effect: { kind: 'heal', amount: 4 }, reachesAFriend: true },
  { def: 161, name: 'Tonic', effect: { kind: 'resource', amount: 2 }, reachesAFriend: false },
  { def: 162, name: 'Smokepot', effect: { kind: 'hidden', pulses: 1 }, reachesAFriend: false },
  { def: 163, name: 'Scroll of Revealing', effect: { kind: 'reveal' }, reachesAFriend: false },
];

export function installConsumables(defs: ConsumableDef[]): void { if (defs.length) catalogue = defs; }
export function consumable(def: number): ConsumableDef | undefined { return catalogue.find((c) => c.def === def); }

/** "heals 4 · you or a friend beside you" — what Use does, in the catalogue's numbers. */
export function effectLine(c: ConsumableDef, resource?: ResourceName): string {
  switch (c.effect.kind) {
    case 'heal': return `heals ${c.effect.amount}${c.reachesAFriend ? ' · you or a friend beside you' : ''}`;
    case 'resource': return `+${c.effect.amount} ${resource ? resource.toLowerCase() : 'resource'}`;
    case 'hidden': return `hidden ${c.effect.pulses} turn${c.effect.pulses === 1 ? '' : 's'}`;
    case 'reveal': return 'shows every hidden thing and trap on the field · one use';
  }
}

/** fk-sim-wasm `item_card`: one item at one tier, the catalogue's own numbers (10 §6.3–6.4). */
export interface ItemCard {
  def: number;
  name: string;
  family: Record<string, unknown> | string;
  slot?: 'MainHand' | 'OffHand' | 'Torso' | 'Head' | 'Cape' | 'Trinket' | 'Hands' | 'Feet' | 'Neck' | 'Trinket2' | 'Legs' | 'Waist' | null;
  /** docs/71 INV2: every slot it may be worn in, its own first (a ring either trinket slot, a dagger either hand). */
  slots?: string[];
  /** The tiers it comes in (1-based). */
  tiers?: number[];
  class_mask: number;
  two_handed: boolean;
  stats: { base_dmg: number; reach: number; range: number; guard: number; hp: number; move_tiles: number; power: number; focus: number; guard_pierce: number; resource_max?: number; push_resist?: number };
  traits: string[];
  consumable?: Record<string, number> | string | null;
}

const SLOT_WORDS: Record<string, string> = { MainHand: 'Main hand', OffHand: 'Off hand', Torso: 'Torso', Head: 'Head', Cape: 'Cape', Trinket: 'Ring or charm', Hands: 'Hands', Feet: 'Feet', Neck: 'Neck', Trinket2: 'Ring or charm', Legs: 'Legs', Waist: 'Waist' };
const CLASS_WORDS = ['Fighter', 'Rogue', 'Wizard', 'Cleric', 'Ranger', 'Paladin', 'Druid', 'Bard'];
/** docs/104 NC1: every class's bit (`ClassMask` is a full u8 now). */
const ALL_CLASSES = 0xff;
/** fk-sim `Trait`s gear can give (`gear_trait`), in its doc comments' words. */
const TRAIT_WORDS: Record<string, string> = {
  Laden: 'Laden: slower through sand, snow, rough ground, mud, water and thicket',
  SureFooted: 'Sure-footed: the sky and season never slow you',
  KeenEyed: 'Keen-eyed: see two squares further through fog, storm and snow',
  Lightbearer: 'Lightbearer: lights the squares around you in the dark, and is seen from afar there',
  Slippery: 'Slippery: break away from an enemy beside you for nothing',
};
const signed = (n: number) => (n > 0 ? `+${n}` : `${n}`);

/** An item card in words: where it goes, who may use it, and what it does (only what `derive_stats` reads). */
export interface ItemDetails { kind: string; classes: string; effects: string[] }

export function itemDetails(c: ItemCard): ItemDetails {
  // docs/72 IT16: a catalogue piece reads from its own entry (powers, active and use included)
  const cat = catalogueDetails(c.def);
  if (cat) return cat;
  const s = c.stats;
  const effects: string[] = [];
  if (c.consumable != null) {
    const [d] = consumablesFromWasm([{ def: c.def, name: c.name, effect: c.consumable, reaches_a_friend: typeof c.consumable === 'object' && 'Heal' in c.consumable }]);
    const line = d ? effectLine(d) : '';
    if (line) effects.push(line.charAt(0).toUpperCase() + line.slice(1));
  }
  if (c.slot === 'MainHand') {
    effects.push(`${s.base_dmg} weapon damage`);
    effects.push(s.range > 0 ? `Ranged: shoots ${s.range} squares` : `Melee: reach ${Math.max(1, s.reach)}`);
    if (s.guard_pierce) effects.push(`Ignores ${s.guard_pierce} guard`);
  }
  if (s.guard) effects.push(`${signed(s.guard)} guard`);
  if (s.hp) effects.push(`${signed(s.hp)} max HP`);
  if (s.move_tiles) effects.push(`${signed(s.move_tiles)} move`);
  if (s.power) effects.push(`${signed(s.power)} power (weapon damage)`);
  if (s.focus) effects.push(`${signed(s.focus)} focus (spell damage and heals)`);
  if (s.resource_max) effects.push(`${signed(s.resource_max)} class resource`);
  if (s.push_resist) effects.push(`${signed(s.push_resist)} against being pushed`);
  // docs/71 INV3: the dagger may also be worn in the off hand
  if (c.slot === 'MainHand' && c.slots?.includes('OffHand')) effects.push('In the off hand: +1 power (+2 at masterwork) beside a one-handed weapon');
  for (const t of c.traits) effects.push(TRAIT_WORDS[t] ?? t);
  const kind = c.consumable != null ? 'Consumable · used up in a fight' : c.slot ? `${SLOT_WORDS[c.slot]}${c.two_handed ? ' · two-handed' : ''}` : 'Item';
  const classes = (c.class_mask & ALL_CLASSES) === ALL_CLASSES ? 'Any class' : CLASS_WORDS.filter((_, i) => c.class_mask & (1 << i)).join(', ');
  return { kind, classes, effects };
}

/** A catalogue piece in the details' words, without waiting for the wasm card. */
export function catalogueDetails(def: number): ItemDetails | undefined {
  const it = catalogueItem(def);
  if (!it) return undefined;
  const d = describeItem(it);
  const effects = [...d.worn, ...d.powers];
  if (d.active) effects.push(`${d.active.name} (${d.active.head}): ${d.active.effects.join('; ')}`);
  if (d.use?.when === 'fight') effects.push(`Use in a fight — ${d.use.ability.name}: ${d.use.ability.effects.join('; ')}`);
  else if (d.use) effects.push(`${d.use.verb} in camp: ${d.use.line}`);
  return { kind: d.kind, classes: d.classes, effects };
}

/** One stack in the bag: same def, how many are left, the row ids still usable (first is used next). */
export interface BagStack {
  def: number; name: string; line: string; left: number; spent: number; items: number[]; reachesAFriend: boolean;
  /** docs/72: the ability a catalogue piece casts (`10000 + def`), aimed like a spell. */
  ability?: number;
}

/** The unit's bag (frozen at load) as stacks by def, in catalogue order; spent items count but are not offered. */
export function bagStacks(bags: readonly BagView[] | undefined, unit: number | undefined, resource?: ResourceName): BagStack[] {
  const bag = bags?.find((b) => b.unit === unit);
  if (!bag) return [];
  const out = new Map<number, BagStack>();
  for (const it of bag.items) {
    const c = consumable(it.def);
    const cat = catalogueItem(it.def);
    const f = fightUse(cat);
    const fresh: BagStack = cat
      ? { def: it.def, name: cat.name, line: shortLine(cat), left: 0, spent: 0, items: [], reachesAFriend: f?.range === 'reach', ...(f ? { ability: itemAbilityId(it.def) } : {}) }
      : { def: it.def, name: c?.name ?? `item ${it.def}`, line: c ? effectLine(c, resource) : '', left: 0, spent: 0, items: [], reachesAFriend: !!c?.reachesAFriend };
    const s = out.get(it.def) ?? fresh;
    if (it.used) s.spent++;
    else { s.left++; s.items.push(it.item); }
    out.set(it.def, s);
  }
  return [...out.values()].sort((a, b) => a.def - b.def);
}

/** Whether the unit still has anything to use (the hotbar then offers Use Item). */
export function hasConsumables(bags: readonly BagView[] | undefined, unit: number): boolean {
  return !!bags?.find((b) => b.unit === unit)?.items.some((i) => !i.used);
}

/** A haul row as the HUD needs it (fk-world `haul_item`, docs/25 D3). */
export interface HaulRowIn { id: bigint; fixture: number; openedBy: bigint; def: number; tier: number; name: string; relic: boolean }
export interface HaulLine { id: string; def: number; name: string; tier: number; relic: boolean; finder: string; fixture: number }

/** The haul, newest first, with who found each piece (hero names by hero id). */
export function haulLines(rows: readonly HaulRowIn[], heroNames: Record<number, string>): HaulLine[] {
  return [...rows].sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0)).map((r) => ({
    id: r.id.toString(), def: r.def, name: r.name, tier: r.tier, relic: r.relic, finder: heroNames[Number(r.openedBy)] ?? 'The party', fixture: r.fixture,
  }));
}

/** "Mara found a Fine Tonic" / "Mara found Hobb's Mace" — the pickup moment's line. */
export function foundLine(finder: string, name: string, relic: boolean): string {
  const article = relic || /^[A-Z][a-z]+'s /.test(name) ? '' : /^[aeiou]/i.test(name) ? 'an ' : 'a ';
  return `${finder} found ${article}${name}`;
}

/**
 * Haul rows that arrived after the panel first saw this encounter's haul: what the pickup moment
 * announces. `seen` is updated in place; the first call for an encounter only primes it.
 */
export function freshHaul<T extends { id: bigint }>(rows: readonly T[], seen: Set<string>, primed: boolean): T[] {
  const fresh: T[] = [];
  for (const r of rows) {
    const k = r.id.toString();
    if (seen.has(k)) continue;
    seen.add(k);
    if (primed) fresh.push(r);
  }
  return fresh.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

